import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Star, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { PaymentMethods } from './PaymentMethods';
const socialProofAvatars = ['https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=120&q=80', 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=120&q=80', 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=120&q=80', 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=120&q=80', 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=120&q=80'];
export function Hero() {
  return <section className="relative overflow-hidden bg-slate-900 pt-16 pb-12 sm:pt-32 sm:pb-24 lg:pb-32" data-id="element-74">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-75">
        <div className="absolute -top-1/2 -right-1/4 w-full h-full bg-brand-500/10 blur-3xl rounded-full" data-id="element-76" />
        <div className="absolute -bottom-1/2 -left-1/4 w-full h-full bg-brand-500/10 blur-3xl rounded-full" data-id="element-77" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-78">
        <div className="text-center max-w-4xl mx-auto" data-id="element-79">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-80">
            <span className="inline-flex items-center rounded-full bg-brand-500/10 px-3 py-1 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-brand-400 ring-1 ring-inset ring-brand-500/20 mb-6" data-id="element-81">
              Your Entire Teaching Career. Solved.
            </span>
            <h1 className="font-display font-extrabold uppercase tracking-tight text-white sm:text-6xl lg:text-7xl mb-6 sm:mb-8 text-balance text-[24px]" data-id="element-82">
              STOP PLANNING EVERY CLASS{' '}
              <span className="block text-brand-400" data-id="element-83">FROM SCRATCH.</span>
            </h1>
          </motion.div>

          {/* Main product mockup — between title and subtitle */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.25
        }} className="relative mx-auto w-[80%] max-w-2xl mb-8 sm:mb-10" data-id="element-84">
            <motion.img animate={{
            y: [0, -10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} src="https://cdn.magicpatterns.com/uploads/4dgsTa4MyNzpLdaXEDcrK2/Gemini_Generated_Image_8lvaxp8lvaxp8lva-Photoroom.png" alt="Stretching Ready Complete System Bundle" className="w-full drop-shadow-2xl" data-id="element-85" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} data-id="element-86">
            <p className="sm:text-lg sm:leading-8 text-slate-300 max-w-2xl mx-auto mb-8 sm:mb-10 text-balance text-[12px]" data-id="element-87">
              <strong className="text-white" data-id="element-88">Stretching Ready</strong> — 6
              volumes and 5 bonuses bundle. 600+ pages, 6 complete stretching
              volumes, 5 powerful bonuses and everything a stretching instructor
              needs — prepared in minutes, taught with confidence.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex flex-col items-center gap-4" data-id="element-89">
            <Button size="xl" className="w-full sm:w-auto group" onClick={() => {
            window.location.href = 'https://stretching-ready.impultienda.ar/checkout';
          }} data-id="element-90">
              Get Instant Access Now
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" data-id="element-91" />
            </Button>

            {/* Payment methods */}
            <PaymentMethods className="mt-4" data-id="element-92" />

            <div className="flex items-center gap-1.5 mt-3 text-sm text-slate-400" data-id="element-93">
              <ShieldCheck className="h-5 w-5 text-brand-400" data-id="element-94" />
              <span data-id="element-95">Secure Payment · 7-Day Guarantee</span>
            </div>

            {/* Social proof — avatar stack + rating */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5" data-id="element-96">
              <div className="flex -space-x-3" data-id="element-97">
                {socialProofAvatars.map((src, i) => <img key={i} src={src} alt="" aria-hidden="true" loading="lazy" className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover ring-2 ring-slate-900" data-id="element-98" />)}
              </div>
              <div className="text-center sm:text-left" data-id="element-99">
                <div className="flex items-center justify-center sm:justify-start gap-2" data-id="element-100">
                  <div className="flex text-amber-400" data-id="element-101">
                    {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-current" data-id="element-102" />)}
                  </div>
                  <span className="text-base sm:text-lg font-bold text-white" data-id="element-103">
                    4.9/5
                  </span>
                </div>
                <p className="mt-0.5 text-sm sm:text-base font-semibold text-white" data-id="element-104">
                  +1,000 instructors worldwide 🌍
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}